import { useState, useMemo } from 'react';
import { usePageMeta, useSchemaOrg } from '@/hooks/usePageMeta';
import PageShell from '@/components/feature/PageShell';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, articleCategories } from '@/mocks/articles';

export default function Articles() {
  const [activeTag, setActiveTag] = useState('all');

  usePageMeta({
    title: '蜜桃知识 - 选购指南 | 保存技巧 | 产区知识 | 美丽的小蜜桃',
    description: '美丽的小蜜桃知识专栏：水蜜桃选购指南、保存技巧、产区介绍、营养价值、美食食谱、送礼文化。关于蜜桃的一切这里都有，甜蜜桃子推荐。',
    keywords: '水蜜桃选购指南,水蜜桃保存方法,水蜜桃产区,桃子营养价值,蜜桃甜品食谱,桃子送礼文化',
    canonicalUrl: 'https://meilixiaomitao.com/articles',
  });

  useSchemaOrg({
    organization: true,
    website: true,
    webpage: true,
    breadcrumb: [
      { name: '首页', url: 'https://meilixiaomitao.com/' },
      { name: '蜜桃知识', url: 'https://meilixiaomitao.com/articles' },
    ],
  });

  const categoryMap: Record<string, string> = {
    all: '全部文章',
    guide: '选购指南',
    storage: '保存技巧',
    regions: '产区知识',
    health: '健康知识',
    recipes: '美食食谱',
    culture: '文化礼俗',
  };

  const filteredArticles = useMemo(
    () =>
      activeTag === 'all'
        ? articles
        : articles.filter((a) => {
            const catSlug = articleCategories.find((c) => c.name === a.category)?.slug;
            return catSlug === activeTag;
          }),
    [activeTag]
  );

  return (
    <PageShell>
      <div className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '蜜桃知识' },
              ]}
            />
          </div>

          <div className="text-center mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              蜜桃知识
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              选购指南、保存技巧、产区知识，关于蜜桃的一切这里都有
            </p>
          </div>

          {/* Tag Filters */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex flex-wrap justify-center gap-2 bg-background-100 rounded-full px-1 py-1">
              {articleCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveTag(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeTag === cat.slug
                      ? 'bg-background-50 text-primary-600 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <ArticleCard key={article.id} {...article} />
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-read-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400 text-sm">该分类暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}